import React, { useState, useEffect } from 'react';
import { Clock, CalendarOff, Plus, Trash2, GripVertical, X } from 'lucide-react';
import { Calendar } from '../../components/ui/calendar';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { getTimeSlots, createTimeSlot, updateTimeSlot, deleteTimeSlot, getBlockedDates, createBlockedDate, deleteBlockedDate } from '../../services/api';
import { toast } from 'sonner';

const Availability = () => {
  // Time Slots State
  const [timeSlots, setTimeSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [newSlotTime, setNewSlotTime] = useState('');
  
  // Blocked Dates State
  const [blockedDates, setBlockedDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [blockReason, setBlockReason] = useState('');
  const [showBlockDialog, setShowBlockDialog] = useState(false);

  // Fetch Time Slots
  const fetchTimeSlots = async () => {
    try {
      const data = await getTimeSlots(false);
      setTimeSlots(data);
    } catch (error) {
      toast.error('Failed to load time slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  // Fetch Blocked Dates
  const fetchBlockedDates = async () => {
    try {
      const data = await getBlockedDates();
      setBlockedDates(data);
    } catch (error) {
      toast.error('Failed to load blocked dates');
    } finally {
      setLoadingDates(false);
    }
  };

  useEffect(() => {
    fetchTimeSlots();
    fetchBlockedDates();
  }, []);

  // Time Slot Handlers
  const handleAddTimeSlot = async () => {
    if (!newSlotTime.trim()) return;
    
    try {
      const maxOrder = timeSlots.length > 0 ? Math.max(...timeSlots.map(s => s.display_order)) : -1;
      await createTimeSlot({ 
        time_value: newSlotTime.trim(), 
        display_order: maxOrder + 1,
        is_active: true 
      });
      toast.success('Time slot added');
      fetchTimeSlots();
      setNewSlotTime('');
      setShowAddSlot(false);
    } catch (error) {
      toast.error('Failed to add time slot');
    }
  };

  const handleToggleSlot = async (slot) => {
    try {
      await updateTimeSlot(slot.id, { is_active: !slot.is_active });
      setTimeSlots(prev => prev.map(s => 
        s.id === slot.id ? { ...s, is_active: !s.is_active } : s
      ));
      toast.success(slot.is_active ? 'Time slot disabled' : 'Time slot enabled');
    } catch (error) {
      toast.error('Failed to update time slot');
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Delete this time slot?')) return;
    
    try {
      await deleteTimeSlot(slotId);
      setTimeSlots(prev => prev.filter(s => s.id !== slotId));
      toast.success('Time slot deleted');
    } catch (error) {
      toast.error('Failed to delete time slot');
    }
  };

  // Blocked Date Handlers
  const handleDateClick = (date) => {
    if (!date) return;
    
    const dateStr = date.toISOString().split('T')[0];
    const isBlocked = blockedDates.some(b => b.date === dateStr);
    
    if (isBlocked) {
      // Unblock
      handleUnblockDate(dateStr);
    } else {
      // Show dialog to block
      setSelectedDate(date);
      setBlockReason('');
      setShowBlockDialog(true);
    }
  };

  const handleBlockDate = async () => {
    if (!selectedDate) return;
    
    const dateStr = selectedDate.toISOString().split('T')[0];
    
    try {
      const result = await createBlockedDate({ 
        date: dateStr, 
        reason: blockReason || null 
      });
      setBlockedDates(prev => [...prev, result]);
      toast.success('Date blocked');
      setShowBlockDialog(false);
      setSelectedDate(undefined);
    } catch (error) {
      toast.error('Failed to block date');
    }
  };

  const handleUnblockDate = async (dateStr) => {
    try {
      await deleteBlockedDate(dateStr);
      setBlockedDates(prev => prev.filter(b => b.date !== dateStr));
      toast.success('Date unblocked');
    } catch (error) {
      toast.error('Failed to unblock date');
    }
  };

  // Get blocked dates as Date objects for calendar
  const blockedDateObjects = blockedDates.map(b => new Date(b.date + 'T00:00:00'));

  // Calendar modifiers
  const modifiers = {
    blocked: blockedDateObjects
  };

  const modifiersStyles = {
    blocked: {
      backgroundColor: 'var(--accent-blush)',
      color: 'var(--text-heading)',
      fontWeight: '600'
    }
  };

  if (loadingSlots || loadingDates) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton rounded-lg" />
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="h-64 skeleton rounded-xl" />
          <div className="h-64 skeleton rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="heading-1 mb-1">Availability</h1>
        <p className="body-small text-[var(--text-secondary)]">Manage your booking time slots and days off</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Time Slots Section */}
        <div className="bg-white rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="heading-3 flex items-center gap-2">
              <Clock size={18} />
              Time Slots
            </h2>
            <button
              onClick={() => setShowAddSlot(true)}
              className="btn-primary !py-2 !px-3 !min-w-0"
              data-testid="add-time-slot-btn"
            >
              <Plus size={16} />
              Add
            </button>
          </div>

          <p className="body-small text-[var(--text-secondary)] mb-4">
            These are the available booking times shown to clients
          </p>

          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {timeSlots.map((slot) => (
              <div 
                key={slot.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  slot.is_active 
                    ? 'bg-[var(--bg-subtle)] border-[var(--border-light)]' 
                    : 'bg-gray-50 border-gray-200 opacity-60'
                }`}
                data-testid={`time-slot-${slot.id}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`font-medium ${slot.is_active ? 'text-[var(--text-heading)]' : 'text-gray-400 line-through'}`}>
                    {slot.time_value}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleSlot(slot)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                      slot.is_active 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`toggle-slot-${slot.id}`}
                  >
                    {slot.is_active ? 'Active' : 'Inactive'}
                  </button>
                  <button
                    onClick={() => handleDeleteSlot(slot.id)}
                    className="p-2 rounded-lg hover:bg-red-50 transition-colors text-red-500"
                    data-testid={`delete-slot-${slot.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
            
            {timeSlots.length === 0 && (
              <p className="text-center py-8 text-[var(--text-secondary)]">
                No time slots configured
              </p>
            )}
          </div>

          {/* Add Time Slot Dialog */}
          <Dialog open={showAddSlot} onOpenChange={setShowAddSlot}>
            <DialogContent className="max-w-sm bg-white rounded-2xl">
              <DialogTitle className="heading-3 mb-4">Add Time Slot</DialogTitle>
              <div className="space-y-4">
                <div>
                  <label className="block body-small font-medium mb-1">Time</label>
                  <input
                    type="text"
                    value={newSlotTime}
                    onChange={(e) => setNewSlotTime(e.target.value)}
                    placeholder="e.g., 9:00 AM"
                    className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
                    data-testid="new-slot-time-input"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowAddSlot(false)}
                    className="px-4 py-2 text-[var(--text-secondary)]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddTimeSlot}
                    disabled={!newSlotTime.trim()}
                    className="btn-primary !py-2 !px-4 !min-w-0 disabled:opacity-50"
                    data-testid="confirm-add-slot-btn"
                  >
                    Add
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Blocked Dates Section */}
        <div className="bg-white rounded-xl p-6">
          <h2 className="heading-3 flex items-center gap-2 mb-5">
            <CalendarOff size={18} />
            Days Off
          </h2>

          <p className="body-small text-[var(--text-secondary)] mb-4">
            Click on a date to block/unblock it. Blocked dates are highlighted.
          </p>

          <div className="flex justify-center mb-4">
            <Calendar
              mode="single"
              selected={undefined}
              onSelect={handleDateClick}
              modifiers={modifiers}
              modifiersStyles={modifiersStyles}
              disabled={[{ before: new Date() }]}
              className="rounded-xl border border-[var(--border-light)]"
              data-testid="availability-calendar"
            />
          </div>

          {/* Blocked Dates List */}
          {blockedDates.length > 0 && (
            <div className="mt-4">
              <h3 className="body-small font-medium mb-2">Blocked Dates:</h3>
              <div className="space-y-2 max-h-[150px] overflow-y-auto">
                {blockedDates
                  .filter(b => new Date(b.date) >= new Date(new Date().toDateString()))
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map((blocked) => (
                    <div 
                      key={blocked.id}
                      className="flex items-center justify-between p-2 bg-[var(--bg-subtle)] rounded-lg"
                      data-testid={`blocked-date-${blocked.date}`}
                    >
                      <div>
                        <span className="font-medium text-sm">
                          {new Date(blocked.date + 'T00:00:00').toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </span>
                        {blocked.reason && (
                          <span className="text-xs text-[var(--text-secondary)] ml-2">
                            ({blocked.reason})
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleUnblockDate(blocked.date)}
                        className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-red-500"
                        data-testid={`unblock-${blocked.date}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Block Date Dialog */}
          <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
            <DialogContent className="max-w-sm bg-white rounded-2xl">
              <DialogTitle className="heading-3 mb-4">Block Date</DialogTitle>
              <div className="space-y-4">
                <p className="text-[var(--text-secondary)]">
                  Block{' '}
                  <span className="font-medium text-[var(--text-heading)]">
                    {selectedDate?.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'long', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                  ?
                </p>
                <div>
                  <label className="block body-small font-medium mb-1">Reason (optional)</label>
                  <input
                    type="text"
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    placeholder="e.g., Holiday, Vacation"
                    className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
                    data-testid="block-reason-input"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowBlockDialog(false)}
                    className="px-4 py-2 text-[var(--text-secondary)]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBlockDate}
                    className="btn-primary !py-2 !px-4 !min-w-0"
                    data-testid="confirm-block-date-btn"
                  >
                    Block Date
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Availability;
